import { useEffect, useRef, useState } from "react";
import { api, is429, retryAfterSec } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SMS_MARKETING_LABEL, SMS_TRANSACTIONAL_LABEL, SMS_PHONE_DISCLOSURE } from "@/config/smsConsent";

const REASONS = ["Program question", "Application support", "Payment support", "Existing student support", "Media or partnership", "Other"];

export default function Contact() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", reason: "Program question", message: "",
    sms_marketing_consent: false, sms_transactional_consent: false,
    sms_consent_language_marketing: "", sms_consent_language_transactional: "",
    consent_privacy_terms: false,
    hp_field: "",
  });
  const [busy, setBusy] = useState(false);
  const submitBtnRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const set = (k) => (e) => setF((d) => ({ ...d, [k]: e.target.value }));

  useEffect(() => () => { if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.email || !f.message) { toast.error("Please complete name, email and message."); return; }
    if (!f.consent_privacy_terms) { toast.error("Please agree to the Privacy Policy and Terms & Conditions to send your message."); return; }
    setBusy(true);
    if (cooldownTimerRef.current) { clearTimeout(cooldownTimerRef.current); cooldownTimerRef.current = null; }
    try {
      await api.post("/contact", f);
      toast.success("Message sent. We'll be in touch.");
      setF({
        full_name: "", email: "", phone: "", reason: "Program question", message: "",
        sms_marketing_consent: false, sms_transactional_consent: false,
        sms_consent_language_marketing: "", sms_consent_language_transactional: "",
        consent_privacy_terms: false,
        hp_field: "",
      });
    } catch (err) {
      if (is429(err)) {
        const sec = retryAfterSec(err);
        // Entered form data is preserved (state not reset). Button stays disabled briefly.
        toast.error("Too many attempts were received. Please wait a moment and try again.", { id: "rl-contact", duration: 8000 });
        cooldownTimerRef.current = setTimeout(() => {
          setBusy(false);
          cooldownTimerRef.current = null;
          try { submitBtnRef.current?.focus(); } catch { /* ignore */ }
        }, sec * 1000);
        return;
      }
      toast.error("Unable to send message. Please try again.");
    } finally { if (!cooldownTimerRef.current) setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="sect-navy pt-32 pb-24 min-h-screen">
      <div className="bcu-container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="bcu-eyebrow">Contact</p>
          <h1 className="mt-5 text-4xl md:text-6xl lg:text-7xl font-display leading-[1.02]">Get in touch with <span className="text-[#F7931E]">the team.</span></h1>
          <p className="mt-6 text-[#C7CFDD] max-w-[52ch]">For program questions, application support or partnership inquiries. This form is not the program application.</p>

          <div className="mt-8 border-t border-[rgba(255,255,255,0.10)] pt-6 text-[#C7CFDD] text-sm leading-relaxed max-w-[52ch]">
            <p>The fastest way to reach our team is the form on this page. A member of the enrollment team responds during business hours. Direct business contact details are provided to applicants once an application has been reviewed.</p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bcu-card p-8 md:p-10 space-y-6" data-testid="contact-form">
          <input type="text" className="hidden" value={f.hp_field} onChange={set("hp_field")} tabIndex={-1} autoComplete="off" />
          <div className="grid md:grid-cols-2 gap-6">
            <label className="block"><span className="bcu-label">Full name *</span><input className="bcu-input" data-testid="contact-name" value={f.full_name} onChange={set("full_name")} autoComplete="name" /></label>
            <label className="block"><span className="bcu-label">Email *</span><input type="email" className="bcu-input" data-testid="contact-email" value={f.email} onChange={set("email")} autoComplete="email" inputMode="email" /></label>
            <label className="block"><span className="bcu-label">Phone</span><input type="tel" className="bcu-input" value={f.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel" /></label>
            <label className="block"><span className="bcu-label">Reason for contacting</span>
              <select className="bcu-input" value={f.reason} onChange={set("reason")}>
                {REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
          </div>
          <label className="block"><span className="bcu-label">Message *</span><textarea rows="6" className="bcu-input" data-testid="contact-message" value={f.message} onChange={set("message")} /></label>

          {/* A2P 10DLC — SMS consent checkboxes, independent + optional. */}
          <div className="border border-[rgba(255,255,255,0.10)] rounded-lg p-5 bg-[#0A0A0A]">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">SMS / Text Message Preferences</div>
            <p className="mt-2 text-xs text-[#7A8296] leading-relaxed">{SMS_PHONE_DISCLOSURE}</p>
            <div className="mt-4 space-y-3">
              <label className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
                <input
                  type="checkbox"
                  data-testid="contact-sms-transactional"
                  checked={!!f.sms_transactional_consent}
                  onChange={(e) => setF((d) => ({
                    ...d,
                    sms_transactional_consent: e.target.checked,
                    sms_consent_language_transactional: e.target.checked ? SMS_TRANSACTIONAL_LABEL : "",
                  }))}
                  className="mt-1 h-4 w-4 accent-[#F7931E]"
                />
                <span className="text-sm text-[#C7CFDD] leading-relaxed">{SMS_TRANSACTIONAL_LABEL}</span>
              </label>
              <label className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
                <input
                  type="checkbox"
                  data-testid="contact-sms-marketing"
                  checked={!!f.sms_marketing_consent}
                  onChange={(e) => setF((d) => ({
                    ...d,
                    sms_marketing_consent: e.target.checked,
                    sms_consent_language_marketing: e.target.checked ? SMS_MARKETING_LABEL : "",
                  }))}
                  className="mt-1 h-4 w-4 accent-[#F7931E]"
                />
                <span className="text-sm text-[#C7CFDD] leading-relaxed">{SMS_MARKETING_LABEL}</span>
              </label>
            </div>
          </div>

          <label className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
            <input
              type="checkbox"
              data-testid="contact-consent-privacy-terms"
              checked={!!f.consent_privacy_terms}
              onChange={(e) => setF((d) => ({ ...d, consent_privacy_terms: e.target.checked }))}
              className="mt-1 h-4 w-4 accent-[#F7931E]"
            />
            <span className="text-sm text-[#C7CFDD] leading-relaxed">
              I agree to the <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> and <Link to="/terms" className="underline hover:text-white">Terms &amp; Conditions</Link>. <span className="text-[#F7931E]">*</span>
            </span>
          </label>

          <button ref={submitBtnRef} type="submit" disabled={busy} className="bcu-btn-primary" data-testid="contact-submit">{busy ? "Sending…" : (<>Send message <ArrowRight className="h-4 w-4" /></>)}</button>
        </form>
      </div>
    </div>
  );
}
